import { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import {
  Wrench, Heart, X, ChevronRight, Filter,
  Play, Pause, RefreshCw, KeyRound, Power, Info, Pencil, Server, RotateCcw, Eraser, FileCode, ArrowRightLeft,
  PanelLeftClose, PanelLeftOpen, Menu as MenuIcon, LayoutGrid, MessagesSquare, Users,
  Sun, Moon, Bug, Bot,
  Settings as SettingsIcon, Building2, Waves, Plus, Search, Boxes, Kanban, Target, EyeOff, Gauge,
} from 'lucide-react';
import type { SessionInfo, AgentState, PoolInfo, AgentLaneStatus } from '../api/types';
import { api } from '../api/client';
import { McpPanel } from './McpPanel';
import { ArcturianDock } from './ArcturianPanel';
import { useNow, computeNudgeEta, formatMmSs } from '../hooks/useNow';
import { AgentInfoModal } from './AgentInfoModal';
import { ResetModal } from './ResetModal';
import { MoveDialog } from './MoveDialog';
import { AgentHoverTooltip, HOVER_CAPABLE, getCachedMetrics, fetchAgentMetrics, type AgentMetrics } from './AgentHoverTooltip';
import { PromptInspectorModal } from './PromptInspectorModal';
import { UsageWidget } from './UsageWidget';
import { UsageMeter } from './UsageMeter';
import { useBusStatus } from '../hooks/useEventBus';
import { setThemeMode } from '../lib/accent';
import './SessionList.css';
import { useInstanceMode } from '../hooks/useInstanceMode';
import { useServiceAvailable } from '../hooks/useServiceOrigin';

function heartbeatAgo(ts: number): string {
  const sec = Math.max(0, Math.floor(Date.now() / 1000 - ts));
  if (sec < 60) return `vor ${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `vor ${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `vor ${hr}h`;
  const d = Math.floor(hr / 24);
  return `vor ${d}d`;
}

// Aktionen, die den Agenten-Prozess neu starten. Nur für sie greift die
// Doppelklick-Sperre — ein zweiter Klick auf „Suspend" ist harmlos, ein
// zweiter Neustart trifft einen gerade hochfahrenden Agenten.
// Arcturian ist bewusst auf EIN Konto beschränkt (Alex 2026-08-05: „wenn dann
// soll das eh nur bei apopovic.aut@gmail.com aktiv sein"). Es ist seine
// persönliche operative Sprach-/Avatar-Ebene, kein Produktmerkmal für Mandanten
// — und die Handlungsseite ist ohnehin bis zum Ownership-Eintrag in #4431
// gesperrt. Client-seitige Sichtbarkeit, keine Sicherheitsgrenze: die echte
// Grenze zieht der Server (Realtime-Grant, Authority, Receipts).
const ARCTURIAN_OWNER = 'apopovic.aut@gmail.com';

const RESTARTING_ACTIONS = new Set(['restart', 'hard-reset', 'kill']);
// Die Sperre wird durch den ZUSTAND des Agenten gelöst, nicht durch eine Uhr:
// sobald er sich wieder meldet, ist der Neustart vorbei (Alex 2026-08-05 zu
// festen Wartezeiten: „ich warte mal 12 Sekunden, ich warte mal 20 Sekunden,
// das ist kompletter Bullshit" — er hat recht, eine geschätzte Dauer ist keine
// Aussage über den Prozess).
//
// Die beiden Zeiten unten sind deshalb KEINE Schätzung der Neustartdauer:
// - GRACE: kurze Blindzeit direkt nach dem Auslösen. Ohne sie würde der noch
//   unveränderte „ready"-Zustand des alten Prozesses die Sperre sofort lösen.
// - BACKSTOP: reine Notbremse für den Fall, dass der Agent sich NIE wieder
//   meldet. Ohne sie bliebe der Eintrag dauerhaft gesperrt.
const RESTART_LOCK_GRACE_MS = 4_000;
const RESTART_LOCK_BACKSTOP_MS = 90_000;
// Zustände, die belegen, dass der Agent wieder lebt.
const AGENT_BACK_STATES = new Set(['ready', 'thinking', 'tool', 'waiting', 'idle']);

interface Props {
  sessions: SessionInfo[];
  agentStates: Record<string, AgentState>;
  pools: PoolInfo[];
  activeSession: string | null;
  userEmail: string;
  /** Email aliases of the authenticated user — used to expand the
   *  "Meine" ownership match so a user logged in via any alias sees
   *  all agents owned via any other alias. Optional; falls back to
   *  strict email match when absent. */
  userAliases?: string[];
  /** Explicit allowed_agents whitelist from /auth/me. Guests like Anna
   *  Sacher own no agents but have a single bot whitelisted — without
   *  this, "Meine" was empty for them, forcing a click to "Alle" before
   *  they saw anything. Bots in this list count as theirs. */
  userAllowedAgents?: string[];
  onSelect: (name: string) => void;
  onNew: () => void;
  onDashboard?: () => void;
  onSettings?: () => void;
  onRbac?: () => void;
  onTenants?: () => void;
  onAdminUsers?: () => void;
  onHealth?: () => void;
  onHistory?: () => void;
  onFragments?: () => void;
  onActivity?: () => void;
  onWorkboard?: () => void;
  onGoals?: () => void;
  laneStatus?: Map<string, AgentLaneStatus>;
  // Count of open/unprocessed issues per agent (#180) — blue badge on the row.
  openIssues?: Map<string, number>;
  // Operator-only view (Alex/superadmin/admin). The Work-Board declared-state
  // badges (blockiert/wartet/fertig) + issue counts are FEDERATION-operator
  // signals — a normal tenant customer (Daniel) read "BLOCKIERT" as "I may not
  // use this agent" (Cloud IACP ddabc4cb). Default false → hidden for customers.
  isOperator?: boolean;
  /** Live-busy of the open agent (from its conversation stream) — overrides a stale
   *  idle agent-state so the selected row's dot matches its detail header. */
  activeBusy?: boolean;
  onCollab?: () => void;
  onNewCollab?: () => void;
  onEditAgent?: (session: SessionInfo) => void;
  /** Operator-only manual CLI/LLM handoff for an existing agent (#562). */
  onSwitchAgent?: (session: SessionInfo) => void;
  onMulti?: () => void;
  // Report the currently-visible (RBAC + search-filtered) agent names so the
  // ambient voice-narrator can scope its roaming to "what the operator sees".
  onVisibleAgentsChange?: (names: string[]) => void;
  error: string | null;
}

type SortMode = 'activity' | 'name';
type GroupMode = 'server' | 'owner' | 'flat';
type AgentFilter = 'mine' | 'all';

const ACTIVE_STATES = new Set(['thinking', 'tool', 'ready', 'error']);

function statePriority(state: string): number {
  switch (state) {
    case 'thinking':
    case 'tool': return 0;
    case 'error': return 1;
    case 'ready': return 2;
    default: return 3;
  }
}

// "Last active" = most recent sign of life. The backend's last_active_at
// is max(last_activity, last_pane_change_at): human input OR autonomous
// pane motion (an agent grinding through IACP turns bumps the latter but
// not last_activity, which is user-input-only). Falling back to
// last_activity alone made busy agents read as "Nd ago" while live
// (Alex 2026-06-15: ArkturianClaude active seconds ago showed "4d ago").
// Fallback chain keeps working against older backends mid-rollout.
function getActivityTs(session: SessionInfo): number {
  const active = session.last_active_at;
  if (active && active > 0) return active;
  return session.last_activity && session.last_activity > 0
    ? session.last_activity
    : Number(session.created);
}

function timeAgo(session: SessionInfo): string {
  const ts = getActivityTs(session);
  const diff = Date.now() / 1000 - ts;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function SessionList({
  sessions,
  agentStates,
  pools,
  activeSession,
  userEmail,
  userAliases,
  userAllowedAgents,
  onSelect,
  onNew,
  onDashboard,
  onSettings,
  onRbac,
  onTenants,
  onAdminUsers,
  onHealth,
  onHistory,
  onFragments,
  onActivity,
  onWorkboard,
  onGoals,
  laneStatus,
  openIssues,
  isOperator = false,
  activeBusy,
  onCollab,
  onNewCollab,
  onEditAgent,
  onSwitchAgent,
  onMulti,
  onVisibleAgentsChange,
  error,
}: Props) {
  const instanceMode = useInstanceMode();
  const issueAvailable = useServiceAvailable('issue');
  // Konten-Auslastung nur, wenn die Instanz den Dienst überhaupt hat. Der
  // Modus-Test allein reicht nicht: Davids Instanz meldet `federation`
  // (peer_count 1), das Fenster erschien also — und blieb leer, weil der
  // Aufruf beim Betreiber landete und dort 401 bekam. Leer und unerklärt ist
  // schlechter als gar nicht da.
  const usageAvailable = useServiceAvailable('usage');
  // Der gespeicherte Standard ist 'server' — auf einer Standalone-Instanz
  // gäbe es dann eine Gruppierung ohne den Knopf, sie zu ändern. Flat ist
  // dort die einzige sinnvolle Sicht; die Nutzerwahl in localStorage bleibt
  // unangetastet, falls dieselbe Person später eine Föderation öffnet.
  useEffect(() => {
    if (instanceMode === 'standalone') setGroupMode((g) => (g === 'server' ? 'flat' : g));
  }, [instanceMode]);
  // Glass header overlay measurement (see .sidebar-header) — height → --sb-hdr-h.
  const sbRootRef = useRef<HTMLElement>(null);
  const sbHeaderRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const c = sbRootRef.current;
    if (!c) return;
    const update = () => c.style.setProperty('--sb-hdr-h', `${sbHeaderRef.current?.offsetHeight ?? 0}px`);
    update();
    const ro = new ResizeObserver(update);
    if (sbHeaderRef.current) ro.observe(sbHeaderRef.current);
    return () => ro.disconnect();
  }, []);
  // Persist search across navigation — on mobile the user often types a
  // query, opens an agent, navigates back, and expects the filter to still
  // be there. Was cleared on remount (state lives in this component).
  const [search, setSearchState] = useState(() => localStorage.getItem('cloud-v2-search') || '');
  const setSearch = useCallback((value: string) => {
    setSearchState(value);
    if (value) localStorage.setItem('cloud-v2-search', value);
    else localStorage.removeItem('cloud-v2-search');
  }, []);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  // Pools are hidden by default (Alex 2026-06-28: rarely needed) — toggled from the
  // nav menu. Persisted so the choice sticks across reloads.
  const [showPools, setShowPools] = useState(() => {
    try { return localStorage.getItem('cloud-show-pools') === '1'; } catch { return false; }
  });
  const toggleShowPools = () => setShowPools((v) => {
    const next = !v;
    try { localStorage.setItem('cloud-show-pools', next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });
  // Usage-Widget ein-/ausblendbar, gleiches Muster wie Pools (Alex 2026-07-30:
  // "den usage status der agenten soll man so wie bei pool optional haben").
  // Anders als Pools jedoch default SICHTBAR: Alex hat das Widget selbst
  // angefordert (#426) und nutzt es — "optional" heißt hier abschaltbar, nicht
  // standardmäßig weg. Wer es nicht braucht, blendet es einmal aus und die
  // Wahl hält über Reloads.
  const [showUsage, setShowUsage] = useState(() => {
    try { return localStorage.getItem('cloud-show-usage') !== '0'; } catch { return true; }
  });
  const toggleShowUsage = () => setShowUsage((v) => {
    const next = !v;
    try { localStorage.setItem('cloud-show-usage', next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });
  // Quick light/dark toggle in the header (Alex 2026-06-26). Reads the resolved
  // theme off <html data-theme> and flips it; full mode (incl. system) stays in
  // Settings. setThemeMode writes localStorage + re-applies data-theme.
  const [themeResolved, setThemeResolved] = useState<'light' | 'dark'>(
    () => (document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'),
  );
  const toggleTheme = useCallback(() => {
    const next = themeResolved === 'dark' ? 'light' : 'dark';
    setThemeMode(next);
    setThemeResolved(next);
  }, [themeResolved]);
  // Debug console, now integrated as a real header button (Alex 2026-06-26: the old
  // floating gear overlapped the theme toggle). Bridged to DebugConsole via window
  // events: we listen for the error count + dispatch the toggle.
  const [debugErrors, setDebugErrors] = useState(0);
  useEffect(() => {
    const onErrors = (e: Event) => setDebugErrors((e as CustomEvent<number>).detail || 0);
    window.addEventListener('cloud-debug-errors', onErrors);
    return () => window.removeEventListener('cloud-debug-errors', onErrors);
  }, []);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; name: string; node?: string } | null>(null);
  const [infoSession, setInfoSession] = useState<SessionInfo | null>(null);
  // Session name pending a Soft-Wipe Reset (Commit ac97a16). Same Reset
  // dialog as AgentDetail header — surfaced here for sidebar-driven flow
  // so Alex doesn't have to open the agent first to wipe its context.
  const [resetSession, setResetSession] = useState<string | null>(null);
  // Agent selected for a node-move (opens MoveDialog → Cloud /migrate).
  const [moveSession, setMoveSession] = useState<SessionInfo | null>(null);
  // Session whose prompt inspector is open — surfaced in the sidebar menu
  // so it matches AgentDetail's header menu (which already had Prompt).
  const [promptSession, setPromptSession] = useState<{ name: string; pool: string | undefined } | null>(null);
  const [ctxStatus, setCtxStatus] = useState('');
  // Agenten mit laufender Neustart-Aktion. Ref für die Prüfung (der Callback
  // hat leere Abhängigkeiten und sähe State sonst veraltet), State fürs
  // Ausgrauen im Menü.
  // Avatar-Sichtbarkeit direkt im Menue schaltbar (Alex 2026-08-05: "wenn ich
  // links unten auf Menü klicke, da habe ich die ganzen Buttons und da würde
  // ich gerne das Ding deaktivieren können"). Die Einstellung liegt weiterhin
  // in localStorage — dieselbe Quelle wie der Schalter in den Settings, damit
  // beide dasselbe anzeigen.
  const [arcHidden, setArcHidden] = useState(() => {
    try { return localStorage.getItem('cloud-arcturian-hidden') !== '0'; } catch { return true; }
  });
  // Der Schalter in den Einstellungen schreibt dieselbe Quelle. Ohne dieses
  // Mitlauschen zeigte die Kachel dort Geänderte nicht an und beide
  // Bedienelemente widersprächen sich.
  useEffect(() => {
    const sync = () => {
      try { setArcHidden(localStorage.getItem('cloud-arcturian-hidden') !== '0'); } catch { /* ignore */ }
    };
    window.addEventListener('cloud-arcturian-visibility-change', sync);
    return () => window.removeEventListener('cloud-arcturian-visibility-change', sync);
  }, []);

  const toggleArcturian = useCallback(() => {
    setArcHidden((prev) => {
      const next = !prev;
      try { localStorage.setItem('cloud-arcturian-hidden', next ? '1' : '0'); } catch { /* private mode */ }
      window.dispatchEvent(new CustomEvent('cloud-arcturian-visibility-change'));
      return next;
    });
  }, []);

  const lifecycleBusyRef = useRef<Set<string>>(new Set());
  const lockedAtRef = useRef<Record<string, number>>({});
  const [lifecycleBusy, setLifecycleBusy] = useState<Set<string>>(new Set());
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);

  // Desktop hover info card (Alex 2026-06-23) — model / node / size on hover.
  const [hoverCard, setHoverCard] = useState<{ session: SessionInfo; rect: DOMRect } | null>(null);
  const [hoverMetrics, setHoverMetrics] = useState<AgentMetrics | undefined>(undefined);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverNameRef = useRef<string | null>(null);
  const onRowEnter = useCallback((e: React.MouseEvent, s: SessionInfo) => {
    if (!HOVER_CAPABLE) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      hoverNameRef.current = s.name;
      setHoverCard({ session: s, rect });
      const cached = getCachedMetrics(s.name);
      setHoverMetrics(cached);
      if (!cached) {
        fetchAgentMetrics(s.name)
          // Ignore a late fetch if the pointer already moved to another row.
          .then((m) => { if (hoverNameRef.current === s.name) setHoverMetrics(m); })
          .catch(() => {});
      }
    }, 350);
  }, []);
  const onRowLeave = useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHoverCard(null);
  }, []);
  useEffect(() => () => { if (hoverTimer.current) clearTimeout(hoverTimer.current); }, []);

  const [mcpPanel, setMcpPanel] = useState<string | null>(null);

  // Rate-limit countdown — one shared tick for the whole list. If any bot
  // is currently in rate_limited_transient with a backend-stamped first-
  // detect time, we re-render this column every second so the per-row
  // tooltip stays current. While no bot is throttled (the common case)
  // the hook registers no interval at all.
  const anyRateLimited = useMemo(
    () => Object.values(agentStates).some(
      (s) => s.state === 'rate_limited_transient' && (s.rate_limit_stamped_at ?? 0) > 0
    ),
    [agentStates]
  );
  // Also tick every second when any agent has a live now/next status, so
  // the per-row elapsed counter ("· 24s") stays live between SSE pushes.
  const anyLiveStatus = useMemo(
    () => Object.values(agentStates).some((s) => !!s.live_status),
    [agentStates]
  );
  const now = useNow(anyRateLimited || anyLiveStatus);

  // MCP-Health: per-bot auth-api state. Refreshed every 60s.
  type McpHealthEntry = {
    name: string; status: string; role: string | null;
    department: string | null; owner_email: string;
  };
  const [mcpHealth, setMcpHealth] = useState<Record<string, McpHealthEntry>>({});
  useEffect(() => {
    let active = true;
    const fetchHealth = async () => {
      try {
        // Via api.mcpHealth: bearer + 401-retry. Cloud gates this endpoint (#591),
        // and a 401 now throws into the catch below — the badge stays off, which
        // is the intended graceful degradation.
        const data = await api.mcpHealth();
        if (!active) return;
        const map: Record<string, McpHealthEntry> = {};
        for (const a of (data.agents || [])) map[a.name] = a;
        setMcpHealth(map);
      } catch {}
    };
    fetchHealth();
    const id = setInterval(fetchHealth, 60_000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const releaseLock = useCallback((name: string) => {
    if (!lifecycleBusyRef.current.has(name)) return;
    const next = new Set(lifecycleBusyRef.current);
    next.delete(name);
    lifecycleBusyRef.current = next;
    const { [name]: _drop, ...rest } = lockedAtRef.current;
    lockedAtRef.current = rest;
    setLifecycleBusy(next);
  }, []);

  // Der eigentliche Auslöser: der Agent meldet sich wieder. Damit hängt die
  // Sperre am beobachteten Zustand statt an einer geschätzten Dauer — meldet
  // er sich nach 3 s, ist nach 3 s wieder freigegeben; braucht er 40 s, bleibt
  // sie 40 s.
  useEffect(() => {
    if (lifecycleBusy.size === 0) return;
    const now = Date.now();
    lifecycleBusy.forEach((name) => {
      const since = lockedAtRef.current[name] ?? 0;
      // Blindzeit: unmittelbar nach dem Auslösen steht im Zustand noch der
      // ALTE Prozess. Ohne sie löst dessen „ready" die Sperre sofort.
      if (now - since < RESTART_LOCK_GRACE_MS) return;
      const st = agentStates[name]?.state;
      // Solange der Server 'restarting' meldet, bleibt gesperrt — unabhängig
      // von der Blindzeit. Erst ein wieder lebender Zustand gibt frei.
      if (st === 'restarting') return;
      if (st && AGENT_BACK_STATES.has(st)) releaseLock(name);
    });
  }, [agentStates, lifecycleBusy, releaseLock]);

  const doAction = useCallback(async (name: string, action: string) => {
    // Doppelklick-Sperre für neustartende Aktionen (Alex 2026-08-05, Post #4461):
    // Restart/Hard-Reset/Kill waren ungebremst mehrfach auslösbar. Zwei
    // Neustarts hintereinander treffen einen Agenten, der gerade hochfährt —
    // im besten Fall dauert es länger, im schlechteren wird eine gerade
    // wiederhergestellte Nachricht erneut abgeschnitten. Das gilt schon für
    // zwei Klicks von Hand und erst recht neben Clouds automatischem Neustart.
    //
    // Bewusst pro AGENT und nicht global: eine laufende Aktion auf Agent A darf
    // Agent B nicht blockieren.
    if (RESTARTING_ACTIONS.has(action) && lifecycleBusyRef.current.has(name)) {
      setCtxStatus(`${name}: Neustart läuft bereits`);
      setCtxMenu(null);
      window.setTimeout(() => setCtxStatus(''), 2500);
      return;
    }
    if (RESTARTING_ACTIONS.has(action)) {
      lockedAtRef.current = { ...lockedAtRef.current, [name]: Date.now() };
      lifecycleBusyRef.current = new Set(lifecycleBusyRef.current).add(name);
      setLifecycleBusy(lifecycleBusyRef.current);
    }
    setCtxStatus(`${action}...`);
    setCtxMenu(null);
    try {
      if (action === 'resume') await api.sessions.resume(name);
      else if (action === 'suspend') await api.sessions.suspend(name);
      else if (action === 'restart') await api.sessions.restart(name);
      else if (action === 'hard-reset') {
        // Hard Reset = wipe conversation history, no memory update,
        // Persona/MCPs preserved. Soft Reset (ResetModal) lives below
        // and uses /api/sessions/{name}/reset instead. IACP 49f6ac81.
        if (!window.confirm(`Hard Reset agent "${name}"?\n\nWipes conversation history (no memory update) and restarts fresh.\nPersona, MCPs and Tools are preserved.\n\nFor a memory-aware reset that keeps the conversation context, use Soft Reset instead.`)) {
          setCtxStatus('');
          return;
        }
        await api.sessions.restart(name, { wipe_history: true });
      }
      else if (action === 'kill') {
        if (!window.confirm(`Kill agent "${name}"? This terminates the session.`)) {
          setCtxStatus('');
          return;
        }
        await api.sessions.kill(name);
      }
      else if (action === 'fix-mcp') await fetch(`/api/sessions/${name}/fix-mcp`, { method: 'POST' });
      else if (action === 'login-manual') {
        // Manual login (#228): open the agent + its login modal in URL-only mode —
        // no auto-flow, no waiting. Cross-component via window event.
        onSelect(name);
        setTimeout(() => window.dispatchEvent(new CustomEvent('cloud-open-login', { detail: { session: name, mode: 'manual' } })), 250);
        setCtxStatus('');
        return;
      }
      else if (action === 'login') {
        // Auto-login takes ~15-20s (Chrome spawn + xdotool + CDP click
        // + code paste). Show "in progress…" the whole time.
        setCtxStatus('login: running auto-flow (~20s)');
        const res = await fetch('/api/auth/login-request', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session: name, force: true }),
        });
        const data = await res.json();
        if (data.status === 'ok') setCtxStatus('login: success');
        else setCtxStatus(`login: ${data.error || data.status || 'failed'}`);
        setTimeout(() => setCtxStatus(''), 5000);
        return;
      }
      setCtxStatus(`${action}: done`);
    } catch {
      setCtxStatus(`${action}: failed`);
    } finally {
      // finally, nicht am Ende des try: doAction hat mehrere frühe returns
      // (abgebrochene Rückfragen, Login-Wege) — ohne finally bliebe der Agent
      // dort dauerhaft gesperrt.
      if (RESTARTING_ACTIONS.has(action)) {
        // Notbremse. Der Normalfall ist der Effekt unten, der auf den
        // zurückkehrenden Agenten reagiert.
        window.setTimeout(() => releaseLock(name), RESTART_LOCK_BACKSTOP_MS);
      }
    }
    setTimeout(() => setCtxStatus(''), 2000);
  }, []);

  // MCP load/save now lives inside the shared <McpPanel>; this just
  // opens it for the chosen session.
  const openMcpPanel = useCallback((name: string) => {
    setCtxMenu(null);
    setMcpPanel(name);
  }, []);
  // Always "all" — the Meine/Alle toggle was removed from the UI (Alex
  // 2026-06-22: "verwende immer alle"). Kept as state so the existing
  // filter logic + auto-switch effect stay intact.
  const [agentFilter, setAgentFilter] = useState<AgentFilter>('all');
  // True if the user has EVER explicitly toggled the filter (a stored key
  // exists). Gates the auto-default below: we only auto-switch a user who
  // has never made a choice — never override a deliberate "Meine" pick.
  const explicitFilterChoiceRef = useRef<boolean>(
    localStorage.getItem('cloud-v2-agent-filter') !== null
  );
  const handleFilterChange = (mode: AgentFilter) => {
    explicitFilterChoiceRef.current = true;
    setAgentFilter(mode);
    localStorage.setItem('cloud-v2-agent-filter', mode);
  };

  // Collapsed "rail" mode (desktop): shrink the sidebar to a narrow strip
  // of agent avatars + status dots so the conversation gets the space and
  // the focus stays on the agent (Alex 2026-06-19). Click an avatar to
  // switch; hover shows the name. Persisted per-browser.
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem('cloud-v2-sidebar-collapsed') === '1',
  );
  const toggleCollapsed = () => setCollapsed((c) => {
    const next = !c;
    localStorage.setItem('cloud-v2-sidebar-collapsed', next ? '1' : '0');
    return next;
  });

  // Node-level mute. Stored as the set of HIDDEN node names — empty set
  // means "show everything", which is the default. Lets Alex hide pdrei
  // (a heavily-populated node) without losing the agent count totals.
  const [hiddenNodes, setHiddenNodes] = useState<Set<string>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('cloud-v2-hidden-nodes') || '[]');
      return new Set(saved);
    } catch { return new Set(); }
  });
  const [nodeFilterOpen, setNodeFilterOpen] = useState(false);
  const toggleHiddenNode = (node: string) => {
    setHiddenNodes((prev) => {
      const next = new Set(prev);
      if (next.has(node)) next.delete(node);
      else next.add(node);
      localStorage.setItem('cloud-v2-hidden-nodes', JSON.stringify([...next]));
      return next;
    });
  };
  const showAllNodes = () => {
    setHiddenNodes(new Set());
    localStorage.setItem('cloud-v2-hidden-nodes', '[]');
  };
  const hideAllNodes = () => {
    const all = availableNodes.map(([n]) => n);
    setHiddenNodes(new Set(all));
    localStorage.setItem('cloud-v2-hidden-nodes', JSON.stringify(all));
  };

  // Per-dimension Owner filter — mirror of the node/server filter (Alex
  // 2026-06-22: "neben Owner ein Button für Filter"). owner_email/owner,
  // stripped to the local part, "unassigned" when empty (same key as the
  // owner grouping below).
  // All of the operator's own emails collapse into ONE "ich" bucket so his
  // bots group + filter as a single owner (Alex 2026-06-22: "drei emails sind
  // von mir, die könnte man gruppieren"). `myEmails` (canonical + aliases) is
  // declared below; ownerKeyOf is only ever CALLED from the memos further
  // down, by which point it's initialised.
  const ownerKeyOf = (s: SessionInfo): string => {
    const o = (s.owner_email || s.owner || '').toLowerCase();
    if (o && myEmails.has(o)) return 'ich';
    return o ? o.split('@')[0] : 'unassigned';
  };
  const [hiddenOwners, setHiddenOwners] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('cloud-v2-hidden-owners') || '[]')); }
    catch { return new Set(); }
  });
  const [ownerFilterOpen, setOwnerFilterOpen] = useState(false);
  const toggleHiddenOwner = (owner: string) => {
    setHiddenOwners((prev) => {
      const next = new Set(prev);
      if (next.has(owner)) next.delete(owner);
      else next.add(owner);
      localStorage.setItem('cloud-v2-hidden-owners', JSON.stringify([...next]));
      return next;
    });
  };
  const showAllOwners = () => {
    setHiddenOwners(new Set());
    localStorage.setItem('cloud-v2-hidden-owners', '[]');
  };
  const hideAllOwners = () => {
    const all = availableOwners.map(([o]) => o);
    setHiddenOwners(new Set(all));
    localStorage.setItem('cloud-v2-hidden-owners', JSON.stringify(all));
  };
  const [sortMode, setSortMode] = useState<SortMode>(() =>
    (localStorage.getItem('cloud-sort-by') as SortMode) || 'activity'
  );
  const [groupMode, setGroupMode] = useState<GroupMode>(() =>
    (localStorage.getItem('cloud-group-by') as GroupMode) || 'server'
  );
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('cloud-collapsed-groups') || '[]');
      return new Set(saved);
    } catch { return new Set(); }
  });

  const toggleGroup = (node: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(node)) next.delete(node);
      else next.add(node);
      localStorage.setItem('cloud-collapsed-groups', JSON.stringify([...next]));
      return next;
    });
  };

  const handleSortChange = (mode: SortMode) => {
    setSortMode(mode);
    localStorage.setItem('cloud-sort-by', mode);
  };

  const handleGroupChange = (mode: GroupMode) => {
    setGroupMode(mode);
    localStorage.setItem('cloud-group-by', mode);
  };

  // When the server-prefs pull seeds localStorage (cross-device sync,
  // lib/prefs.ts), re-read the synced filter/grouping state so it applies
  // live without a reload (Alex 2026-06-22).
  useEffect(() => {
    const reread = () => {
      try { setHiddenNodes(new Set(JSON.parse(localStorage.getItem('cloud-v2-hidden-nodes') || '[]'))); } catch { /* keep */ }
      try { setHiddenOwners(new Set(JSON.parse(localStorage.getItem('cloud-v2-hidden-owners') || '[]'))); } catch { /* keep */ }
      const g = localStorage.getItem('cloud-group-by');
      if (g === 'server' || g === 'owner' || g === 'flat') setGroupMode(g);
      const s = localStorage.getItem('cloud-sort-by');
      if (s === 'activity' || s === 'name') setSortMode(s);
    };
    window.addEventListener('cloud-prefs-pulled', reread);
    return () => window.removeEventListener('cloud-prefs-pulled', reread);
  }, []);

  const poolInstanceNames = useMemo(() => {
    const names = new Set<string>();
    for (const p of pools) {
      for (const inst of p.instances || []) {
        names.add(inst.name);
      }
    }
    return names;
  }, [pools]);

  // Step 1 of pipeline: ownership filter. "Mine" = strict owner_email match
  // (whitelist hits like Tommy → Defence stay in "All"). Admins see fewer
  // entries here than in "All" — by design, the giant 80+ list is opt-in.
  // Pool-member exclusion uses TWO sources:
  //   1. `s.pool_name` directly on the session (immediate, no Cross-Fetch
  //      wait — kills the flicker when useSessions returns before usePools)
  //   2. `poolInstanceNames` from usePools — covers legacy sessions that
  //      were standalone-spawned and later acquired into a pool dynamically
  //      (no pool_name field set on the registry row).
  const baseList = useMemo(
    () => sessions.filter((s) => !s.pool_name && !poolInstanceNames.has(s.name)),
    [sessions, poolInstanceNames]
  );
  // "Me" = canonical email + any aliases. Multi-email canonical-user
  // (e.g., apopovic.aut + alex@arkturian + spreadyourwingsvideo all
  // resolve to one identity) — bot owned via any alias counts as mine.
  const myEmails = useMemo(() => {
    const set = new Set<string>();
    if (userEmail) set.add(userEmail.toLowerCase());
    for (const a of userAliases || []) {
      if (a) set.add(a.toLowerCase());
    }
    return set;
  }, [userEmail, userAliases]);
  const allowedAgentsSet = useMemo(
    () => new Set(userAllowedAgents || []),
    [userAllowedAgents]
  );
  const isMine = useCallback(
    (s: SessionInfo) => {
      if (myEmails.has((s.owner_email || s.owner || '').toLowerCase())) return true;
      if (allowedAgentsSet.has(s.name)) return true;
      return false;
    },
    [myEmails, allowedAgentsSet]
  );
  const myCount = useMemo(
    () => baseList.filter(isMine).length,
    [baseList, isMine]
  );
  const allCount = baseList.length;

  // Auto-default to "Alle" for a user whose "Meine" is empty (Alex
  // 2026-06-19: a client with one agent under "Alle" but none owned saw a
  // blank list defaulting to "Meine" + a "switch to all" link — bad first
  // impression). Only fires when the user has NEVER explicitly toggled, so
  // a deliberate empty-"Meine" pick is respected. Not persisted: it stays
  // adaptive — the moment the user owns an agent, the next visit defaults
  // back to "Meine". Gated on allCount>0 so we wait for sessions to load
  // (avoids switching against a still-empty list during the first tick).
  useEffect(() => {
    if (explicitFilterChoiceRef.current) return;
    if (agentFilter === 'mine' && myCount === 0 && allCount > 0) {
      setAgentFilter('all');
    }
  }, [agentFilter, myCount, allCount]);

  // Derive the unique node names from baseList so the filter popover
  // only offers nodes that actually have agents. Sorted alphabetically
  // for stable UI ordering — counts shown alongside each row.
  const availableNodes = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of baseList) {
      const node = s.node || 'local';
      counts[node] = (counts[node] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0]));
  }, [baseList]);

  // Distinct owners + counts for the owner-filter popover ("ich" already
  // collapses all of the operator's aliases into one entry).
  const availableOwners = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of baseList) {
      const key = ownerKeyOf(s);
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseList, myEmails]);

  const filtered = useMemo(() => {
    let list = baseList;
    if (agentFilter === 'mine') {
      list = list.filter(isMine);
    }
    if (hiddenNodes.size > 0) {
      list = list.filter((s) => !hiddenNodes.has(s.node || 'local'));
    }
    if (hiddenOwners.size > 0) {
      list = list.filter((s) => !hiddenOwners.has(ownerKeyOf(s)));
    }
    if (search) {
      const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
      // OR-match: each space-separated term is its own filter — typing
      // "cloud content storage" returns the union of all three. AND was
      // tried briefly but Alex's actual usage is "give me a few names,
      // show all of them at once".
      list = list.filter((s) => {
        // Node intentionally NOT in the haystack: searching for "arktur"
        // would otherwise pull in every bot that happens to live on the
        // arkturian-Linode (OnealContract, AiApi, Story, …) alongside
        // the actual Arkturian-prefixed agents. Dedicated node filter
        // popover (funnel icon) covers the per-node use case cleanly.
        // Alex screenshot 110101.
        //
        // window_name ALSO excluded (Alex screenshot 111332): it's the
        // tmux window name = the CLI command, almost always "claude" (or
        // a version like "2.1.173"). Searching "claude" otherwise matched
        // every Claude-Code agent. Identity fields only: name +
        // display_name + department + role.
        const haystack = `${s.name} ${s.display_name || ''} ${s.department || ''} ${s.role || ''}`.toLowerCase();
        return terms.some((term) => haystack.includes(term));
      });
    }
    return list;
  }, [baseList, agentFilter, isMine, search, hiddenNodes, hiddenOwners]);

  // Report the visible (filtered) agent names up so the ambient narrator can
  // scope its roaming to "what the operator sees" (#1215). Stable: `filtered`
  // only recomputes on a real filter change, not on every agent-state poll.
  const visibleNames = useMemo(() => filtered.map((s) => s.name), [filtered]);
  useEffect(() => { onVisibleAgentsChange?.(visibleNames); }, [visibleNames, onVisibleAgentsChange]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sortMode === 'name') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      list.sort((a, b) => {
        const sa = agentStates[a.name]?.state || 'unknown';
        const sb = agentStates[b.name]?.state || 'unknown';
        const pa = statePriority(sa);
        const pb = statePriority(sb);
        if (pa !== pb) return pa - pb;
        const ta = getActivityTs(a);
        const tb = getActivityTs(b);
        if (ta !== tb) return tb - ta;
        // Stable tiebreaker — without this, sessions with identical
        // priority + activity ts (e.g. brand-new sessions, all 0) flip
        // order on every poll and the list flickers visibly.
        return a.name.localeCompare(b.name);
      });
    }
    return list;
  }, [filtered, agentStates, sortMode]);

  const grouped = useMemo(() => {
    const groups: Record<string, SessionInfo[]> = {};
    for (const s of sorted) {
      let key: string;
      if (groupMode === 'owner') {
        // "ich" collapses all of the operator's aliases; others use the
        // email local-part, empty → "unassigned".
        key = ownerKeyOf(s);
      } else {
        key = s.node || 'local';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(s);
    }
    return groups;
  }, [sorted, groupMode]);

  const openCtx = (e: React.MouseEvent | { clientX: number; clientY: number }, s: SessionInfo) => {
    if ('preventDefault' in e) e.preventDefault();
    setCtxMenu({ x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY, name: s.name, node: s.node });
  };

  const handleTouchStart = (e: React.TouchEvent, s: SessionInfo) => {
    const touch = e.touches[0];
    const x = touch.clientX;
    const y = touch.clientY;
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setCtxMenu({ x, y, name: s.name, node: s.node });
      if (navigator.vibrate) navigator.vibrate(50);
    }, 500);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleTouchMove = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const renderSessionItem = (s: SessionInfo) => {
    const stateInfo = agentStates[s.name];
    const rawState = stateInfo?.state || 'unknown';
    const rawDetail = stateInfo?.detail || '';
    // Rate-limit tooltip: only when backend has stamped first-detect.
    // 0 means watchdog cleared after a nudge or before the next scan.
    const rlActive = rawState === 'rate_limited_transient'
      && (stateInfo?.rate_limit_stamped_at ?? 0) > 0;
    const rlEta = rlActive
      ? computeNudgeEta(stateInfo?.rate_limit_stamped_at, stateInfo?.rate_limit_retry_after, now)
      : null;
    const rlTitle = rlActive && rlEta !== null
      ? rlEta > 0
        ? `Rate-limited — nudge in ${formatMmSs(rlEta)}`
        : 'Rate-limited — nudging…'
      : undefined;
    // Status-dot semantics — refactored per Alex's spec (Cloud IACP 628645e1):
    //   🟠 working  — agent is busy RIGHT NOW (state ∈ thinking/tool)
    //   🟢 available — in memory, idle/ready (status running/idle/registered + state ready/idle/unknown)
    //   ⚪ offline   — process not in memory (status ∈ suspended/dead/orphan/recovering)
    //   🔴 error    — needs_login / error / rate_limited (overrides everything)
    //
    // activity_class was the previous source — confused users because a bot
    // they JUST opened in tmux read "cold" until the watchdog picked up
    // pane changes. New semantics is runtime-driven from agent_state +
    // lifecycle status, ignoring how-long-ago-was-the-last-pane-event.
    const state = rawState; // kept for tooltip text; no longer mapped to dot color
    void rawDetail; // unused now that stale_fallback isn't a render path
    const isErrorState = (
      rawState === 'error' ||
      rawState === 'rate_limited_transient' ||
      rawState === 'needs_login' ||
      rawState === 'quota_exhausted'   // pausiert bis Kontingent zurück (18.08.)
    );
    // Lifecycle-dead = process CONFIRMED not running. Stays grey even if a
    // stale thinking-state lingers. But `actually_running === false` alone
    // is a weaker detection signal that lags — it must NOT override a LIVE
    // working state (mirrors AgentDetail header, Alex 2026-06-16).
    const lifecycleDead = (
      s.status === 'suspended' ||
      s.status === 'dead' ||
      s.status === 'orphan' ||
      s.status === 'recovering'
    );
    // The open agent's conversation stream is fresher than the agent-state poll —
    // if it's mid-tool/thinking, show working even when the snapshot lags to idle.
    const isWorking = rawState === 'thinking' || rawState === 'tool'
      || (activeSession === s.name && !!activeBusy && rawState !== 'waiting');
    // Neustart schlägt jede Fenster-Ableitung: der Prozess ist weg oder kommt
    // hoch, das Fenster sagt also nichts Gültiges. Serverseitig ist es aus dem
    // gleichen Grund vor dem Pane-Parse platziert (Cloud e7c7ad5).
    const isRestarting = rawState === 'restarting';
    const dotClass = isRestarting
      ? 'restarting'
      : isErrorState
      ? rawState              // red (error/needs_login/rate_limited_transient)
      : lifecycleDead
        ? 'offline'           // grey — process confirmed dead
        : isWorking
          ? 'working'         // orange + pulse — beats actually_running lag
          : rawState === 'interrupted'
            ? 'interrupted'   // solid amber, no pulse — user stopped it (Esc), needs attention
            : rawState === 'waiting'
              ? 'waiting'     // permission-blocked — "wartet auf dich", not green
              : s.actually_running === false
                ? 'offline'   // grey — not running + not actively working
                : 'available';  // green
    // Lifecycle-status class (#37). When the agent isn't running we
    // gray out the whole row + show a small status pill so the user
    // sees WHY it's dim (idle / suspended / orphan / recovering /
    // dead). Sub-second wake on click — see /api/sessions/{name}/wake.
    const lifecycleStatus = s.status || (s.actually_running === false ? 'unknown' : 'running');
    const isPaused = lifecycleStatus !== 'running' && lifecycleStatus !== 'registered';
    // Declared lane-status (#1251) — surface the human-relevant semantic states as a
    // pill (the live dot already covers working/idle). blocked/waiting/done add meaning.
    const lane = laneStatus?.get(s.name);
    const openCount = openIssues?.get(s.name) || 0;
    const laneBadge = lane && (lane.effective_state === 'blocked' || lane.effective_state === 'done' || lane.effective_state === 'waiting' || lane.effective_state === 'error')
      ? lane.effective_state : null;
    return (
      <div
        key={`${s.node}-${s.name}`}
        className={`session-item lifecycle-${lifecycleStatus}${isPaused ? ' is-paused' : ''}${activeSession === s.name ? ' active' : ''}`}
        onClick={() => { if (!longPressFired.current) onSelect(s.name); }}
        onContextMenu={(e) => openCtx(e, s)}
        onTouchStart={(e) => handleTouchStart(e, s)}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onMouseEnter={(e) => onRowEnter(e, s)}
        onMouseLeave={onRowLeave}
        style={{ WebkitUserSelect: 'none' }}
        // In rail mode the name is hidden, so surface it (+ node) as a
        // native tooltip on hover. Omitted when expanded to avoid a
        // tooltip popping on every row.
        title={collapsed
          ? `${s.display_name || s.name} · ${s.node === 'local' || !s.node ? 'mac' : s.node}`
          : undefined}
      >
        <div
          className={`status-dot ${dotClass}`}
          title={isRestarting
            ? `${stateInfo?.reason || 'Neustart läuft'}${stateInfo?.restarting_seconds != null ? ` · seit ${Math.round(stateInfo.restarting_seconds)} s` : ''}`
            : rlTitle}
        />
        <div className="session-info">
          <div className="session-name">
            {s.display_name || s.name}
            {/* Operator-only signals (Cloud IACP ddabc4cb): issue counts + the
                Work-Board declared-state badges are federation-ops meta, not for
                a tenant customer — Daniel read "BLOCKIERT" as "gesperrt". */}
            {isOperator && openCount > 0 && (
              <span className="sl-issue-badge" title={`${openCount} offene(s) Issue(s) für diesen Agenten`}>{openCount}</span>
            )}
            {/* Neustart sichtbar machen, nicht nur im Tooltip — Alex' eigentliche
                Bitte war „ich sehe nicht, was passiert". Mit laufender Dauer,
                damit erkennbar ist, dass es vorangeht und nicht hängt. NICHT
                operator-only: dass der eigene Agent gerade neu startet, geht
                jeden an, der ihn benutzt. */}
            {isRestarting && (
              <span
                className="restart-badge"
                title={stateInfo?.reason || 'Neustart läuft'}
              >
                startet neu{stateInfo?.restarting_seconds != null ? ` · ${Math.round(stateInfo.restarting_seconds)}s` : ''}
              </span>
            )}
            {isOperator && laneBadge && (
              <span className={`lane-badge lane-badge-${laneBadge}`} title={lane?.summary || undefined}>
                {laneBadge === 'blocked' ? 'blockiert' : laneBadge === 'done' ? 'fertig' : laneBadge === 'error' ? 'ERROR' : 'wartet'}
              </span>
            )}
            {isPaused && (
              <span
                className={`lifecycle-badge lifecycle-badge-${lifecycleStatus}`}
                title={
                  lifecycleStatus === 'idle' ? 'Auto-paused (will wake on next message)' :
                  lifecycleStatus === 'suspended' ? 'User-paused (explicit /wake required)' :
                  lifecycleStatus === 'orphan' ? 'Bot-home on disk, never spawned' :
                  lifecycleStatus === 'recovering' ? 'Watchdog repairing' :
                  lifecycleStatus === 'dead' ? 'Crashed, auto_restart=false' :
                  lifecycleStatus
                }
              >
                {lifecycleStatus}
              </span>
            )}
            {state === 'quota_exhausted' && (
              <span
                className="needs-login-badge"
                title={stateInfo?.reason
                  ? `${stateInfo.reason}${stateInfo.resets_at ? ` — wieder ab ${new Date(stateInfo.resets_at).toLocaleString('de-AT')}` : ''}`
                  : 'Kontingent aufgebraucht — pausiert, Freigabe automatisch'}
              >
                QUOTA
              </span>
            )}
            {state === 'needs_login' && (
              <span
                title={s.must_be_online
                  ? 'Anthropic OAuth expired — auto-login pending'
                  : 'Anthropic OAuth expired — manual login required'}
                className="needs-login-badge"
              >
                AUTH
              </span>
            )}
            {s.must_be_online && (
              <span title="Auto-heal active" className="auto-heal-badge">
                <Wrench size={10} />
              </span>
            )}
            {s.must_be_online && s.last_seen_alive && (
              <span title={`Letzter Heartbeat: ${heartbeatAgo(s.last_seen_alive)}`} className="heartbeat-badge">
                <Heart size={10} />
              </span>
            )}
            {(() => {
              const h = mcpHealth[s.name];
              // Only render when there's a problem — silence is golden.
              if (!h || h.status === 'ok') return null;
              // Severity split (Issue #20, Cloud-Diagnose IACP d2237630):
              //   no_role        → yellow / warn — agent works in practice,
              //                    just missing a role-row in auth-api.
              //                    Bad UX to scream "broken" at the user.
              //   everything else (not_registered, auth_api_unreachable,
              //   error*) → red / bad — real failure modes.
              const isWarn = h.status === 'no_role';
              const cls = isWarn ? 'mcp-badge warn' : 'mcp-badge bad';
              const title = isWarn
                ? 'MCP: agent läuft, aber RBAC-Rolle in auth-api fehlt — Konfiguration unvollständig'
                : `MCP ${h.status} — Agent in auth-api nicht erreichbar / nicht registriert`;
              return (
                <span title={title} className={cls}>⚠ MCP</span>
              );
            })()}
          </div>
          {/* Der Knoten-Name ist Föderations-Vokabular: Auf einer
              Kundeninstanz gibt es genau einen, und „mac" oder „arkserver"
              unter jedem Agenten wäre die erste Frage im Onboarding-Termin. */}
          {instanceMode === 'federation' && (
            <div className="session-role">{s.node === 'local' || !s.node ? 'mac' : s.node}</div>
          )}
          {/* Live now/next ticker (Cloud IACP 308c9a3b) — replaces the
              generic "thinking" with WHAT the agent is doing. Present only
              while working; elapsed counts up live via the shared `now`. */}
          {stateInfo?.live_status && (
            <div className="session-live" title={stateInfo.live_status.next ? `Next: ${stateInfo.live_status.next}` : undefined}>
              <span className="session-live-act">
                {/* activity is null for most agents (whimsy spinner, no
                    Todo) — Cloud IACP cdd6506d. Generic fallback so the
                    ticker isn't just a bare timer. */}
                {stateInfo.live_status.activity || 'arbeitet'}
                {/* Timer only when started_at is a real number. Cloud's
                    extract_live_status omits started_at on a transient
                    capture where the pane shows a Next/Todo line but no
                    spinner-time ("· NaNs" bug, Cloud IACP d6a71da4). The
                    contract: started_at present → timer; absent → no timer. */}
                {Number.isFinite(stateInfo.live_status.started_at) && (
                  <span className="session-live-elapsed"> · {Math.max(0, Math.round(now - stateInfo.live_status.started_at))}s</span>
                )}
              </span>
              {stateInfo.live_status.next && (
                <span className="session-live-next">→ {stateInfo.live_status.next}</span>
              )}
            </div>
          )}
        </div>
        {s.avatar
          ? <img className="session-avatar" src={s.avatar} alt="" />
          : (
            // Initials fallback — only visible in rail mode (CSS), so the
            // expanded list looks unchanged for avatar-less agents.
            <span className="session-avatar session-avatar-initial" aria-hidden="true">
              {(s.display_name || s.name).charAt(0).toUpperCase()}
            </span>
          )}
        <div className="session-meta">{timeAgo(s)}</div>
      </div>
    );
  };

  return (
    <aside ref={sbRootRef} className={`sidebar${collapsed ? ' collapsed' : ''}`}>
      <div className="sidebar-header" ref={sbHeaderRef}>
        <div className="sidebar-logo-row">
          {/* Logo opens Settings (Alex 2026-06-26: "wenn ich auf agentos klicke
              soll settings kommen"). Falls back to a plain label if no handler. */}
          {onSettings ? (
            <button
              type="button"
              className="sidebar-logo sidebar-logo-btn"
              onClick={onSettings}
              title="Einstellungen öffnen"
            >
              Agent<span>OS</span>
            </button>
          ) : (
            <div className="sidebar-logo">
              Agent<span>OS</span>
            </div>
          )}
          <div className="sidebar-header-actions">
            {/* Debug console, integrated as a real button (Alex 2026-06-26 — was a
                floating gear overlapping the theme toggle). Error count → red badge. */}
            <button
              type="button"
              className={`sidebar-collapse-btn sidebar-debug-btn${debugErrors > 0 ? ' has-errors' : ''}`}
              onClick={() => window.dispatchEvent(new CustomEvent('cloud-debug-toggle'))}
              title={debugErrors > 0 ? `Debug-Konsole (${debugErrors} Fehler)` : 'Debug-Konsole'}
              aria-label="Toggle debug console"
            >
              {debugErrors > 0 ? <span className="sidebar-debug-count">{debugErrors}</span> : <Bug size={15} />}
            </button>
            {/* Quick light/dark toggle next to the collapse button (Alex 2026-06-26). */}
            <button
              type="button"
              className="sidebar-collapse-btn sidebar-theme-btn"
              onClick={toggleTheme}
              title={themeResolved === 'dark' ? 'Heller Modus' : 'Dunkler Modus'}
              aria-label={themeResolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {themeResolved === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button
              type="button"
              className="sidebar-collapse-btn"
              onClick={toggleCollapsed}
              title={collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen — mehr Platz für den Agenten'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
          </div>
        </div>

        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search agents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="search-clear"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <X size={10} />
            </button>
          )}
        </div>

        {nodeFilterOpen && (
          <>
            <div className="node-filter-backdrop" onClick={() => setNodeFilterOpen(false)} />
            <div className="node-filter-popover">
              <div className="node-filter-header">
                <span>Server ausblenden</span>
                <span className="node-filter-actions">
                  <button type="button" className="node-filter-reset" onClick={showAllNodes}>Alle an</button>
                  <button type="button" className="node-filter-reset" onClick={hideAllNodes}>Alle aus</button>
                </span>
              </div>
              {availableNodes.map(([node, count]) => {
                const hidden = hiddenNodes.has(node);
                return (
                  <label key={node} className="node-filter-row">
                    <input
                      type="checkbox"
                      checked={!hidden}
                      onChange={() => toggleHiddenNode(node)}
                    />
                    <span className="node-filter-name">{node}</span>
                    <span className="node-filter-count">{count}</span>
                  </label>
                );
              })}
            </div>
          </>
        )}

        {ownerFilterOpen && (
          <>
            <div className="node-filter-backdrop" onClick={() => setOwnerFilterOpen(false)} />
            <div className="node-filter-popover">
              <div className="node-filter-header">
                <span>Owner ausblenden</span>
                <span className="node-filter-actions">
                  <button type="button" className="node-filter-reset" onClick={showAllOwners}>Alle an</button>
                  <button type="button" className="node-filter-reset" onClick={hideAllOwners}>Alle aus</button>
                </span>
              </div>
              {availableOwners.map(([owner, count]) => {
                const hidden = hiddenOwners.has(owner);
                return (
                  <label key={owner} className="node-filter-row">
                    <input
                      type="checkbox"
                      checked={!hidden}
                      onChange={() => toggleHiddenOwner(owner)}
                    />
                    <span className="node-filter-name">{owner === 'ich' ? 'Ich (alle meine)' : owner}</span>
                    <span className="node-filter-count">{count}</span>
                  </label>
                );
              })}
            </div>
          </>
        )}

        {/* Grouping with a per-dimension filter (Alex 2026-06-22: "neben
            Server ein Filter, neben Owner ein Filter"). Sort fixed to Activity,
            scope fixed to "Alle". The tab sets the grouping; the funnel beside
            it opens that dimension's filter popover. */}
        <div className="sort-row">
          {/* Standalone-Ableitung (SaaS-Vertrag #4701): Eine Kundeninstanz hat
              genau einen Knoten. „Nach Server gruppieren" und der Server-Filter
              sind dort keine Funktionen, sondern Föderations-Vokabular, das
              erklärt werden müsste — die Instanz sagt selbst, was sie ist
              (/api/version → mode), und die Sicht folgt ihr. */}
          {instanceMode === 'federation' && (
          <div className="group-cell">
            <button
              className={`sort-btn${groupMode === 'server' ? ' active' : ''}`}
              onClick={() => handleGroupChange('server')}
              title="Nach Server gruppieren"
            >
              Server
            </button>
            <button
              type="button"
              className={`group-filter-btn${hiddenNodes.size > 0 ? ' active' : ''}`}
              onClick={() => setNodeFilterOpen((v) => !v)}
              title="Server filtern — Agenten bestimmter Server ausblenden"
              aria-expanded={nodeFilterOpen}
            >
              <Filter size={11} />
              {hiddenNodes.size > 0 && <span className="filter-count">−{hiddenNodes.size}</span>}
            </button>
          </div>
          )}
          <div className="group-cell">
            <button
              className={`sort-btn${groupMode === 'owner' ? ' active' : ''}`}
              onClick={() => handleGroupChange('owner')}
              title="Nach Owner gruppieren"
            >
              Owner
            </button>
            <button
              type="button"
              className={`group-filter-btn${hiddenOwners.size > 0 ? ' active' : ''}`}
              onClick={() => setOwnerFilterOpen((v) => !v)}
              title="Owner filtern — Agenten bestimmter Owner ausblenden"
              aria-expanded={ownerFilterOpen}
            >
              <Filter size={11} />
              {hiddenOwners.size > 0 && <span className="filter-count">−{hiddenOwners.size}</span>}
            </button>
          </div>
          <button
            className={`sort-btn${groupMode === 'flat' ? ' active' : ''}`}
            onClick={() => handleGroupChange('flat')}
            title="Keine Gruppierung"
          >
            Flat
          </button>
        </div>
      </div>

      {/* MCP Panel — shared component, also used by AgentDetail's header
          menu so both context menus offer identical actions. */}
      {mcpPanel && (
        <McpPanel
          sessionName={mcpPanel}
          onClose={() => setMcpPanel(null)}
          onStatus={(msg) => {
            setCtxStatus(msg);
            setTimeout(() => setCtxStatus(''), 2000);
          }}
        />
      )}

      <div className="session-list" onClick={() => setCtxMenu(null)}>

        {/* Context Menu */}
        {ctxMenu && (
          <>
            <div className="ctx-backdrop" onClick={() => setCtxMenu(null)} />
            <div
              className="ctx-menu"
              style={{ left: ctxMenu.x, top: ctxMenu.y }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="ctx-item"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const s = sessions.find((x) => x.name === ctxMenu.name);
                  if (s) { setCtxMenu(null); setInfoSession(s); }
                }}
              >
                <Info size={14} />
                Info
              </button>
              <button
                className="ctx-item"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const s = sessions.find((x) => x.name === ctxMenu.name);
                  const name = ctxMenu.name;
                  setCtxMenu(null);
                  setPromptSession({ name, pool: s?.pool_name || undefined });
                }}
              >
                <FileCode size={14} />
                Prompt
              </button>
              <div className="ctx-divider" />
              {([
                { label: 'Resume',   action: 'resume',  Icon: Play },
                { label: 'Suspend',  action: 'suspend', Icon: Pause },
                { label: 'Restart',  action: 'restart', Icon: RefreshCw },
                { label: 'Auto-Login',    action: 'login',        Icon: KeyRound },
                { label: 'Login manuell', action: 'login-manual', Icon: KeyRound },
              ] as const).map((item) => {
                // Läuft für diesen Agenten schon ein Neustart, wird der Eintrag
                // ausgegraut und beschriftet — ein Klick, der still nichts tut,
                // wäre schlimmer als ein sichtbar gesperrter Eintrag.
                // Zwei Quellen, bewusst beide: der Serverzustand deckt
                // Neustarts ab, die NICHT aus dieser Oberfläche kamen; die
                // lokale Sperre deckt die Lücke zwischen zwei Abfragen, in der
                // ein Klick sonst noch durchginge (Clouds Hinweis).
                const locked = RESTARTING_ACTIONS.has(item.action)
                  && (lifecycleBusy.has(ctxMenu.name) || agentStates[ctxMenu.name]?.state === 'restarting');
                return (
                  <button
                    key={item.action}
                    className={`ctx-item${locked ? ' ctx-item-locked' : ''}`}
                    disabled={locked}
                    title={locked ? 'Neustart läuft bereits — bitte abwarten' : undefined}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (locked) return;
                      doAction(ctxMenu.name, item.action);
                    }}
                  >
                    <item.Icon size={14} />
                    {locked ? `${item.label} · läuft` : item.label}
                  </button>
                );
              })}
              <div className="ctx-divider" />
              {onEditAgent && (
                <button
                  className="ctx-item"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    const s = sessions.find((x) => x.name === ctxMenu.name);
                    if (s) { setCtxMenu(null); onEditAgent(s); }
                  }}
                >
                  <Pencil size={14} />
                  Edit Agent
                </button>
              )}
              {onSwitchAgent && (
                <button
                  className="ctx-item"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    const s = sessions.find((x) => x.name === ctxMenu.name);
                    if (s) { setCtxMenu(null); onSwitchAgent(s); }
                  }}
                >
                  <Bot size={14} />
                  CLI / LLM wechseln
                </button>
              )}
              <button
                className="ctx-item"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  openMcpPanel(ctxMenu.name);
                }}
              >
                <Server size={14} />
                MCP Server
              </button>
              {/* Knoten-Umzug setzt einen zweiten Knoten voraus — auf einer
                  Standalone-Instanz gibt es kein Ziel (SaaS-Vertrag #4701). */}
              {instanceMode === 'federation' && (
                <button
                  className="ctx-item"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    const s = sessions.find((x) => x.name === ctxMenu.name);
                    setCtxMenu(null);
                    // MoveDialog wants the SessionInfo for source-node display;
                    // fall back to a minimal stub carrying the ctx node so the
                    // "from" label is right even if the row isn't in `sessions`.
                    setMoveSession(s ?? ({ name: ctxMenu.name, node: ctxMenu.node } as SessionInfo));
                  }}
                >
                  <ArrowRightLeft size={14} />
                  Verschieben
                </button>
              )}
              <div className="ctx-divider" />
              <button
                className="ctx-item"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const name = ctxMenu.name;
                  setCtxMenu(null);
                  setResetSession(name);
                }}
                title="Soft Reset — agent updates its /memory, conversation continues"
              >
                <RotateCcw size={14} />
                Soft Reset
              </button>
              <button
                className="ctx-item"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  doAction(ctxMenu.name, 'hard-reset');
                }}
                title="Hard Reset — wipe conversation history, fresh start (no memory update)"
              >
                <Eraser size={14} />
                Hard Reset
              </button>
              <button
                className="ctx-item ctx-destructive"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  doAction(ctxMenu.name, 'kill');
                }}
              >
                <Power size={14} />
                Kill
              </button>
              <div className="ctx-footer">
                {ctxMenu.name}
                {ctxMenu.node && ctxMenu.node !== 'local' && ` · ${ctxMenu.node}`}
              </div>
            </div>
          </>
        )}
        {error && (
          <div style={{ padding: '12px 16px', color: 'var(--color-error)', fontSize: 12 }}>
            API unreachable: {error}
          </div>
        )}

        {agentFilter === 'mine' && myCount === 0 && allCount > 0 && (
          <div className="filter-empty-hint">
            Du besitzt aktuell keine Agents.{' '}
            <button
              type="button"
              className="filter-empty-switch"
              onClick={() => handleFilterChange('all')}
            >
              Alle sichtbaren ({allCount}) anzeigen
            </button>
          </div>
        )}

        {/* Hidden-node notice (Alex 2026-07-22): the −N badge on the filter
            button alone was too subtle — a muted node read as "disappeared"
            (oneal-Vorfall). This unmissable row names the hidden nodes + a
            one-click restore, so a mute is never a silent mystery again. */}
        {hiddenNodes.size > 0 && (
          <div className="hidden-nodes-note">
            <EyeOff size={13} className="hidden-nodes-icon" />
            <span className="hidden-nodes-text">
              {hiddenNodes.size === 1
                ? `1 Node ausgeblendet: ${[...hiddenNodes].join(', ')}`
                : `${hiddenNodes.size} Nodes ausgeblendet: ${[...hiddenNodes].join(', ')}`}
            </span>
            <button type="button" className="hidden-nodes-show" onClick={showAllNodes}>
              Einblenden
            </button>
          </div>
        )}

        {groupMode === 'flat' ? (
          <div className="session-grid">{sorted.map((s) => renderSessionItem(s))}</div>
        ) : (
          Object.entries(grouped)
            // Owner-mode: most-agents first so heavy users float to the top.
            // Server-mode: keep insertion order (mirrors backend node order).
            .sort((a, b) => groupMode === 'owner' ? b[1].length - a[1].length : 0)
            .map(([key, items]) => {
              const collapsed = collapsedGroups.has(key);
              return (
                <div className="server-group" key={key}>
                  <button
                    type="button"
                    className={`server-label${collapsed ? ' collapsed' : ''}`}
                    onClick={() => toggleGroup(key)}
                    aria-expanded={!collapsed}
                  >
                    <span className={`server-chevron${collapsed ? '' : ' expanded'}`}>
                      <ChevronRight size={12} />
                    </span>
                    <span className="server-name">{key === 'ich' ? 'Ich (alle meine)' : key}</span>
                    <span className="server-count">{items.length} agents</span>
                  </button>
                  {!collapsed && <div className="session-grid">{items.map((s) => renderSessionItem(s))}</div>}
                </div>
              );
            })
        )}

        {filtered.length === 0 && !error && (
          <div style={{ padding: '24px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
            No agents found
          </div>
        )}
      </div>

      {showPools && pools.length > 0 && (
        <div className="pool-section">
          <div className="pool-header">Pools</div>
          {pools.map((p) => (
            <div className="pool-row" key={p.name}>
              <div className="pool-name">{p.name}</div>
              <div className="pool-dots">
                {p.instances?.map((inst) => (
                  <div
                    key={inst.name}
                    className={`pool-dot ${inst.is_ready ? 'active' : 'standby'}${inst.acquired_by ? ' acquired' : ''}`}
                    onClick={() => onSelect(inst.name)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      if (inst.acquired_by) {
                        fetch(`/api/pools/${p.name}/release/${inst.name}`, { method: 'POST' })
                          .then(() => setCtxStatus(`${inst.name}: released`))
                          .catch(() => setCtxStatus(`${inst.name}: release failed`));
                        setTimeout(() => setCtxStatus(''), 2000);
                      }
                    }}
                    title={inst.acquired_by ? `${inst.name} (acquired by ${inst.acquired_by}) — right-click to release` : inst.name}
                  />
                )) ?? (
                  <>
                    {Array.from({ length: p.available_count }, (_, i) => (
                      <div key={`a${i}`} className="pool-dot active" />
                    ))}
                    {Array.from({ length: p.size - p.available_count }, (_, i) => (
                      <div key={`s${i}`} className="pool-dot standby" />
                    ))}
                  </>
                )}
                <span className="pool-count">
                  {p.available_count}/{p.size}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {navMenuOpen && (
        <>
          <div className="nav-menu-backdrop" onClick={() => setNavMenuOpen(false)} />
          <div className="nav-menu-panel">
            <div className="nav-menu-grid">
              {/* Avatar an/aus — bewusst hier und nicht nur in den Settings:
                  es ist eine Ansichtssache, die man im Vorbeigehen umschaltet,
                  keine Konfiguration, für die man ein eigenes Menü aufmacht.
                  Bleibt im Menü offen, weil ein Umschalter, der sein eigenes
                  Menü schliesst, das Ergebnis verbirgt. */}
              {userEmail.toLowerCase() === ARCTURIAN_OWNER && (
                <button
                  className={`nav-menu-tile${arcHidden ? '' : ' nav-menu-tile-on'}`}
                  onClick={toggleArcturian}
                  title={arcHidden ? 'Arcturian-Avatar einblenden' : 'Arcturian-Avatar ausblenden'}
                >
                  {arcHidden ? <EyeOff size={20} /> : <Bot size={20} />}
                  <span>Avatar {arcHidden ? 'aus' : 'an'}</span>
                </button>
              )}
              {onDashboard && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onDashboard(); }}>
                  <LayoutGrid size={20} /><span>Dashboard</span>
                </button>
              )}
              {onSettings && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onSettings(); }}>
                  <SettingsIcon size={20} /><span>Settings</span>
                </button>
              )}
              {onRbac && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onRbac(); }}>
                  <KeyRound size={20} /><span>RBAC</span>
                </button>
              )}
              {onTenants && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onTenants(); }}>
                  <Building2 size={20} /><span>Tenants</span>
                </button>
              )}
              {onAdminUsers && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onAdminUsers(); }}>
                  <Users size={20} /><span>Users</span>
                </button>
              )}
              {onActivity && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onActivity(); }}>
                  <Waves size={20} /><span>Agent River</span>
                </button>
              )}
              {onWorkboard && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onWorkboard(); }}>
                  <Kanban size={20} /><span>Work-Board</span>
                </button>
              )}
              {onGoals && issueAvailable && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onGoals(); }}>
                  <Target size={20} /><span>Goals</span>
                </button>
              )}
              <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onNew(); }}>
                <Plus size={20} /><span>Neuer Agent</span>
              </button>
              {onCollab && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onCollab(); }}>
                  <MessagesSquare size={20} /><span>Collab</span>
                </button>
              )}
              {onHealth && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onHealth(); }}>
                  <Heart size={20} /><span>Bot Health</span>
                </button>
              )}
              {onHistory && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onHistory(); }}>
                  <Search size={20} /><span>History</span>
                </button>
              )}
              {onFragments && (
                <button className="nav-menu-tile" onClick={() => { setNavMenuOpen(false); onFragments(); }}>
                  <FileCode size={20} /><span>Fragmente</span>
                </button>
              )}
              {pools.length > 0 && (
                <button
                  className={`nav-menu-tile${showPools ? ' active' : ''}`}
                  onClick={() => { setNavMenuOpen(false); toggleShowPools(); }}
                  title={showPools ? 'Pools ausblenden' : 'Pools einblenden'}
                >
                  <Boxes size={20} /><span>Pools</span>
                </button>
              )}
              {/* Nur für Operatoren — das Widget selbst ist operator-gated
                  (fremde Konto-Auslastung), also wäre ein Schalter für
                  Tenant-User ein Schalter für etwas Unsichtbares. */}
              {isOperator && (
                <button
                  className={`nav-menu-tile${showUsage ? ' active' : ''}`}
                  onClick={() => { setNavMenuOpen(false); toggleShowUsage(); }}
                  title={showUsage ? 'Usage ausblenden' : 'Usage einblenden'}
                >
                  <Gauge size={20} /><span>Usage</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Bottom tab bar (Alex 2026-06-20): Agenten · Gruppen · Multi · Menu.
        * "Agenten" is the current surface, so it's the active tab here. */}
      <div className="sidebar-nav">
        <button className="sidebar-nav-btn active" aria-label="Agenten" title="Agenten">
          <Users size={15} /><span className="sidebar-nav-label">Agenten</span>
        </button>
        {onNewCollab && (
          <button className="sidebar-nav-btn" onClick={onNewCollab} aria-label="Gruppen" title="Gruppenchats">
            <MessagesSquare size={15} /><span className="sidebar-nav-label">Gruppen</span>
          </button>
        )}
        {onMulti && (
          <button className="sidebar-nav-btn" onClick={onMulti} aria-label="Multi" title="Multi-Agent View">
            <LayoutGrid size={15} /><span className="sidebar-nav-label">Multi</span>
          </button>
        )}
        <button
          className={`sidebar-nav-btn${navMenuOpen ? ' active' : ''}`}
          onClick={() => setNavMenuOpen(!navMenuOpen)}
          aria-label="Menu"
          title="Menu"
        >
          <MenuIcon size={15} /><span className="sidebar-nav-label">Menü</span>
        </button>
      </div>

      {ctxStatus && <div className="ctx-status">{ctxStatus}</div>}

      {/* Account-limit usage (#426) — above the connection footer so it's
          always visible without opening a view. Operator-only: the endpoint
          returns EVERY login profile (Alex's AND Jascha's account utilization
          + labels), and Alex' standing rule is that one user must not see
          another's data (same reason the work-board badges + status toasts are
          gated, 1ee8640). Cloud speced it ungated; gating is the consistent
          call until the endpoint filters per user. */}
      {/* Betreiber-Kontenauslastung ist FÖDERATIONS-Betriebsdaten: auf einer
          Kundeninstanz existiert der Dienst nicht — und selbst Alex darf sie
          dort nicht sehen, es sind keine Instanz-Daten (Screenshot 119751:
          Davids Portal zeigte Alex' + Jaschas Konten). */}
      {instanceMode === 'federation' && usageAvailable && isOperator && showUsage && <UsageWidget />}
      {/* Tenant-Kontingent (#4713): selbst-gatend — rendert nur, wenn die
          Instanz GET /api/tenant/usage erfüllt (Kundeninstanzen nach Gap-1-Bau);
          auf der Betreiber-Föderation antwortet der Pfad 404 → unsichtbar. */}
      <UsageMeter />
      {/* Arcturian: quadratisches Feld direkt unter der Liste, über der
          Verbindungs-Fußzeile. Zeigt nur den Avatar-Zustand; der Klick startet
          das Gespräch im bestehenden Sprachmodus. Keine Action-Auslösung — die
          Arcturian-Lane ist bis zum Ownership-Eintrag in #4431 gesperrt. */}
      {userEmail.toLowerCase() === ARCTURIAN_OWNER && <ArcturianDock focus={activeSession} />}
      <div className="sidebar-footer">
        <ConnectionFooter restError={error} />
      </div>

      {hoverCard && (
        <AgentHoverTooltip session={hoverCard.session} metrics={hoverMetrics} rect={hoverCard.rect} placement="right" />
      )}
      {infoSession && (
        <AgentInfoModal
          session={infoSession}
          state={agentStates[infoSession.name]}
          onClose={() => setInfoSession(null)}
        />
      )}
      {resetSession && (
        <ResetModal
          sessionName={resetSession}
          onClose={() => setResetSession(null)}
          onCompleted={() => setResetSession(null)}
        />
      )}
      {moveSession && (
        <MoveDialog
          sessionName={moveSession.name}
          source={moveSession}
          onClose={() => setMoveSession(null)}
          onMoved={(node) => {
            setMoveSession(null);
            setCtxStatus(`${moveSession.name} → ${node === 'local' ? 'mac' : node}`);
            setTimeout(() => setCtxStatus(''), 2500);
            // The parent's poll picks up the new node on the next cycle.
          }}
        />
      )}
      {promptSession && (
        <PromptInspectorModal
          sessionName={promptSession.name}
          poolName={promptSession.pool}
          onClose={() => setPromptSession(null)}
        />
      )}
    </aside>
  );
}

// Footer combining REST-error state with SSE bus status. SSE is the
// primary signal — when it's live, the UI is realtime; reconnecting
// = degraded (polling fallback covers); idle/failed = pure polling
// mode. REST error wins because if the polling itself can't connect,
// nothing is updating at all.
function ConnectionFooter({ restError }: { restError: string | null }) {
  const busStatus = useBusStatus();
  const label = restError
    ? 'Disconnected'
    : busStatus === 'live'
      ? 'Live'
      : busStatus === 'reconnecting' || busStatus === 'connecting'
        ? 'Reconnecting…'
        : busStatus === 'failed'
          ? 'Polling-Only'
          : 'Connected';
  const mod = restError
    ? 'error'
    : busStatus === 'live'
      ? 'live'
      : busStatus === 'reconnecting' || busStatus === 'connecting'
        ? 'reconnecting'
        : busStatus === 'failed'
          ? 'degraded'
          : '';
  return (
    <div className="connection-status">
      <div className={`connection-dot${mod ? ' ' + mod : ''}`} title={`SSE: ${busStatus}`} />
      {label}
    </div>
  );
}
